import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { PlusIcon } from '@heroicons/react/24/outline';
import ClientClaimDocuments from './ClientClaimDocuments';
import ClientClaimNotes from './ClientClaimNotes';
import DateInput from '../components/DateInput';
import ClientClaimEmailTab from './ClientClaimEmailTab';
import ClientClaimWhatsAppTab from './ClientClaimWhatsAppTab';
import { usePermissions } from '../context/PermissionsContext';



// ============================================
// TYPES - Moved to top for better organization
// ============================================
interface Client {
  id: number;
  name: string;
  surname: string;
  email: string;
  mobile_number?: string;
}

interface Policy {
  id: number;
  policy_number: string;
  product_type: string;
  status: string;
}

interface Claim {
  id: number | string;
  created_date: string;
  reference_number: string;
  client_name: string;
  consultant_name: string;
  consultant_id?: number;
  claim_method: string;
  claim_type: string;
  description: string;
  status: string;
  policy_number?: string;
  policy_id?: number;
  product_id?: number;
  product_name?: string;
  date_received: string;
  date_sign_off?: string;
  signed_off_by?: string;
  claims_reported_date?: string;
  underwriters_claim_number?: string;
  total_cost_of_claim?: number;
  claim_excess?: number;
}

interface ClaimDetail extends Claim {
  date_received: string;
  date_sign_off?: string;
  policy_id?: number;
  client_id: number;
  originalDescription?: string;
  product_id?: number;
  product_name?: string;
  claims_reported_date?: string;
  underwriters_claim_number?: string;
  total_cost_of_claim?: number;
  claim_excess?: number;
    consultant_id?: number;
}

interface ClaimConsultant {
  id: number;
  name: string;
  surname: string;
  email: string;
  active?: boolean;
}

interface ClientClaimsTabProps {
  clientId: string;
}

// ============================================
// CLAIM QUESTIONS COMPONENT - Moved before main component
// ============================================
interface ClaimQuestionsProps {
  claimId: string;
  clientId: string;
  clientName: string;
}

interface Question {
  id: number;
  question: string;
  answer?: boolean | null;
  is_required: boolean;
  order: number;
  options?: string[];
}

const ClaimQuestions: React.FC<ClaimQuestionsProps> = ({ claimId, clientId, clientName }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  const [answerTimestamps, setAnswerTimestamps] = useState<Record<number, string>>({});
  const [answerUserNames, setAnswerUserNames] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loggedInUserName, setLoggedInUserName] = useState<string>('');

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const name = user.username || user.email || 'User';
        const surname = user.surname || '';
        setLoggedInUserName(`${name} ${surname}`.trim() || name || 'User');
      } catch (e) {
        setLoggedInUserName('User');
      }
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [claimId]);

  const fetchQuestions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const questionsResponse = await fetch(`${API_URL}/api/claim-definitions`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!questionsResponse.ok) {
        setQuestions([
          { id: 1, question: "Has the incident been reported to the police?", is_required: true, order: 1, options: [] },
          { id: 2, question: "Has the claim been submitted to the insurer?", is_required: true, order: 2, options: [] },
        ]);
        setIsLoading(false);
        return;
      }

      const questionsData = await questionsResponse.json();
      let rawQuestions: any[] = [];
      if (questionsData && questionsData.success && Array.isArray(questionsData.data)) {
        rawQuestions = questionsData.data;
      } else if (questionsData && Array.isArray(questionsData)) {
        rawQuestions = questionsData;
      }
      
      const questionsList = rawQuestions
        .filter((q: any) => q.isActive !== false)
        .map((q: any) => ({
          id: q.id,
          question: q.question || q.text || '',
          is_required: q.isRequired || q.is_required || false,
          order: q.order || 0,
          options: q.options || []
        }));
      
      setQuestions(questionsList);

      const answersResponse = await fetch(`${API_URL}/api/claim-questions/answers/${claimId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      let existingAnswers: Record<number, boolean> = {};
      let existingTimestamps: Record<number, string> = {};
      let existingUserNames: Record<number, string> = {};
      
      if (answersResponse.ok) {
        const answersData = await answersResponse.json();
        if (answersData && answersData.success && Array.isArray(answersData.data)) {
          answersData.data.forEach((answer: any) => {
            existingAnswers[answer.question_id] = answer.answer;
            if (answer.created_at) existingTimestamps[answer.question_id] = answer.created_at;
            if (answer.answered_by) existingUserNames[answer.question_id] = answer.answered_by;
          });
        }
      }
      
      setAnswers(existingAnswers);
      setAnswerTimestamps(existingTimestamps);
      setAnswerUserNames(existingUserNames);
      
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Failed to load questions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, value: boolean) => {
    if (answerTimestamps[questionId] === undefined) {
      setAnswers(prev => ({ ...prev, [questionId]: value }));
    }
  };

  const handleSaveAnswers = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      let signedOffBy = '';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          signedOffBy = `${user.username || user.email || 'User'} ${user.surname || ''}`.trim() || 'User';
        } catch (e) {
          signedOffBy = 'User';
        }
      }

      const answersToSave = Object.entries(answers).map(([questionId, answer]) => ({
        claim_id: parseInt(claimId),
        question_id: parseInt(questionId),
        answer: answer,
        answered_by: signedOffBy
      }));

      const response = await fetch(`${API_URL}/api/claim-questions/answers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: answersToSave }),
      });

      if (!response.ok) throw new Error('Failed to save answers');

      await fetchQuestions();
      setSuccess('Answers saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error saving answers:', error);
      setError(error.message || 'Failed to save answers');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (isLoading) {
    return <div className="bg-white shadow-md rounded-lg p-6 border"><div className="text-center text-gray-500">Loading questions...</div></div>;
  }

  if (questions.length === 0 && !isLoading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border">
        <h4 className="text-md font-semibold text-gray-900 mb-2">Claim Questions</h4>
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">No active claim questions found.</p>
          <p className="text-xs text-gray-400 mt-1">Please add claim questions in Process Definitions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border">
      <h4 className="text-md font-semibold text-gray-900 mb-4">Claim Questions</h4>
      
      {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">{success}</div>}

      <div className="space-y-4">
        {questions.map((question) => {
          const isAnswered = answerTimestamps[question.id] !== undefined;
          const answeredBy = answerUserNames[question.id] || '';
          
          return (
            <div key={question.id} className={`p-4 rounded-lg border transition-colors ${
              isAnswered ? 'bg-gray-100 border-gray-300' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            }`}>
              <p className={`text-sm mb-3 ${isAnswered ? 'text-gray-500' : 'text-gray-900'}`}>{question.question}</p>
              
              <div className="flex space-x-6">
                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === true}
                    onChange={() => handleAnswerChange(question.id, true)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${isAnswered ? 'text-gray-400 border-gray-300 cursor-not-allowed' : 'text-blue-600 border-gray-300 focus:ring-blue-500'}`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>Yes</span>
                </label>
                
                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === false}
                    onChange={() => handleAnswerChange(question.id, false)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${isAnswered ? 'text-gray-400 border-gray-300 cursor-not-allowed' : 'text-blue-600 border-gray-300 focus:ring-blue-500'}`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>No</span>
                </label>
              </div>

              {isAnswered && answerTimestamps[question.id] && (
                <div className="mt-3 flex items-center">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600 border border-gray-300">
                    <svg className="w-3 h-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="mr-1 font-semibold">{answeredBy || loggedInUserName || 'User'}</span>
                    <span>•</span>
                    <span className="ml-1">{formatDateTime(answerTimestamps[question.id])}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(answers).length > Object.keys(answerTimestamps).length && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveAnswers}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Answers'}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const API_URL = process.env.REACT_APP_API_URL || '';

const ClientClaimsTab: React.FC<ClientClaimsTabProps> = ({ clientId }) => {
  const { can } = usePermissions();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const location = useLocation();
  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<ClaimDetail | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'documents' | 'notes' | 'whatsapp' | 'email'>('details');
  const [isSaving, setIsSaving] = useState(false);
  const [signedOffByName, setSignedOffByName] = useState<string>('');
  const [claimsReportedDate, setClaimsReportedDate] = useState<string>('');

  const [isViewProductModalVisible, setIsViewProductModalVisible] = useState(false);
  const [productToView, setProductToView] = useState<any>(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Claims list state
  const [claims, setClaims] = useState<Claim[]>([]);
  const [isLoadingClaims, setIsLoadingClaims] = useState(false);

  // Form state (for modal)
  const [formData, setFormData] = useState({
  policyId: '',
  productId: '',
  claimMethod: '',
  claimType: '',
  description: '',
  assignedTo: '',
  claimsReportedDate: '',
  claimDate: new Date().toISOString().split('T')[0],
  underwritersClaimNumber: '',
  totalCostOfClaim: '',
  claimExcess: '',
});

  // Data state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [employees, setEmployees] = useState<ClaimConsultant[]>([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedPolicyProducts, setSelectedPolicyProducts] = useState<any[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Static options
  const claimMethods = [
    { value: 'face_to_face', label: 'Face To Face' },
    { value: 'email', label: 'E-Mail' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'whatsapp', label: 'Whatsapp' }
  ];

  const claimTypes = [
    { value: 'total_loss', label: 'Total Loss' },
    { value: 'partial_loss', label: 'Partial Loss' }
  ];

  const claimStatuses = [
    { value: 'registered', label: 'Registered' },
    { value: 'send_to_client', label: 'Send To Client' },
    { value: 'sent_to_insurer', label: 'Sent To Insurer' },
    { value: 'schedule_sent_to_client', label: 'Schedule Sent To Client' },
    { value: 'completed', label: 'Completed' }
  ];

  // Fetch initial data
useEffect(() => {
  fetchClientDetails();
  fetchClaims();
  fetchPolicies(clientId);
  fetchEmployees(); // ADD THIS to fetch consultants when component loads
}, [clientId]);

  // ADD THIS - Check if we need to open a specific claim
useEffect(() => {
  const state = location.state as { claimId?: string; activeTab?: string };
  console.log('🔍 ClientClaimsTab - location state:', state);
  console.log('🔍 ClientClaimsTab - initialLoadComplete:', initialLoadComplete);
  console.log('🔍 ClientClaimsTab - claims.length:', claims.length);
  
  if (state?.claimId && initialLoadComplete && claims.length > 0) {
    const claimId = state.claimId;
    console.log('🔍 Opening claim from navigation:', claimId);
    setTimeout(() => {
      handleReferenceClick(claimId);
    }, 200);
    window.history.replaceState({}, document.title);
  }
}, [location.state, initialLoadComplete, claims.length]);

  // Debug useEffect for product modal
  useEffect(() => {
    console.log('🔍 Product Modal State Changed:', { 
      isViewProductModalVisible, 
      productToView: productToView ? 'has data' : 'null' 
    });
  }, [isViewProductModalVisible, productToView]);

  // ✅ Permission guard — after all hooks, before first function
  if (!can('claims', 'read')) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">You do not have permission to view Claims.</p>
      </div>
    );
  }

  const fetchClaims = async () => {
    setIsLoadingClaims(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/claims?clientId=${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let claimsData = [];
      if (response.data && Array.isArray(response.data)) {
        claimsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        claimsData = response.data.data;
      }
      
      const mappedClaims = claimsData.map((item: any) => ({
        id: item.id,
        created_date: item.created_at,
        reference_number: item.reference_number || `CLM-${item.id}`,
                consultant_name: item.consultant_name && item.consultant_surname 
          ? `${item.consultant_name} ${item.consultant_surname}`
          : 'Unassigned',
        consultant_id: item.consultant_id,
        policy_id: item.policy_id,
        policy_number: item.policy_number || 'N/A',
        product_id: item.product_id,
        product_name: item.product_name,
        claim_method: item.claim_method,
        claim_type: item.claim_type,
        description: item.description,
        status: item.status,
        date_received: item.date_received,
        date_sign_off: item.date_sign_off,
        signed_off_by: item.signed_off_by,
        claims_reported_date: item.claims_reported_date || '',
        underwriters_claim_number: item.underwriters_claim_number || '',
        total_cost_of_claim: item.total_cost_of_claim || 0,
        claim_excess: item.claim_excess || 0,
      }));
      
      setClaims(mappedClaims);
      setInitialLoadComplete(true);
    } catch (error) {
      console.error('Error fetching claims:', error);
      setInitialLoadComplete(true);
    } finally {
      setIsLoadingClaims(false);
    }
  };

  const fetchClaimDetail = async (claimId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/claims/${claimId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data.data || response.data;

      console.log('🔍 CLAIM PRODUCT_ID:', data.product_id);
      console.log('🔍 CLAIM POLICY_ID:', data.policy_id);
      console.log('🔍 CLAIM DATA:', data);
      
      const claimProductId = data.product_id;
      const claimPolicyId = data.policy_id;
      
      console.log('📦 Claim data:', { 
        claimProductId, 
        claimPolicyId, 
        product_name: data.product_name 
      });
      
      setSelectedClaim({
        ...data,
                signed_off_by: data.signed_off_by || '',
        consultant_id: data.consultant_id,
        product_id: claimProductId,
        product_name: data.product_name || 'No product selected',
        date_received: data.date_received || data.created_at || '',
        claims_reported_date: data.claims_reported_date || '',
        underwriters_claim_number: data.underwriters_claim_number || '',
        total_cost_of_claim: data.total_cost_of_claim || 0,
        claim_excess: data.claim_excess || 0,
      });
      setSignedOffByName(data.signed_off_by || '');
      
      if (claimPolicyId) {
        await fetchPolicyProducts(claimPolicyId.toString());
        
        setTimeout(() => {
          const currentProducts = selectedPolicyProducts;
          console.log('📊 Current products after fetch:', currentProducts.map((p: any) => ({ 
            id: p.id, 
            product_id: p.product_id, 
            name: p.product_name 
          })));
          
          let matchedProduct: any = null;
          
          if (claimProductId) {
            matchedProduct = currentProducts.find((p: any) => 
              String(p.id) === String(claimProductId) || 
              String(p.product_id) === String(claimProductId)
            );
            
            if (matchedProduct) {
              console.log('✅ Found matching product:', matchedProduct);
              const productName = matchedProduct.product_name || matchedProduct.name || 'No product selected';
              setSelectedClaim((prev: any) => ({
                ...prev!,
                product_id: claimProductId,
                product_name: productName
              }));
            } else {
              console.log(`⚠️ No matching product found for ID: ${claimProductId}`);
              setSelectedClaim((prev: any) => ({
                ...prev!,
                product_id: claimProductId,
                product_name: prev?.product_name || 'Product not found'
              }));
            }
          } else {
            console.log('ℹ️ No product_id on claim');
          }
        }, 100);
      } else {
        setSelectedPolicyProducts([]);
      }
    } catch (error) {
      console.error('Error fetching claim details:', error);
      setError('Failed to load claim details');
    }
  };

  const fetchPolicies = async (clientId: string) => {
    setIsLoadingPolicies(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/claims/policies?clientId=${clientId}&status=active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let policiesData = [];
      if (Array.isArray(response.data)) {
        policiesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        policiesData = response.data.data;
      }
      
      setPolicies(policiesData);
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  const fetchPolicyProducts = async (policyId: string) => {
    if (!policyId) {
      setSelectedPolicyProducts([]);
      return;
    }
    
    setIsLoadingProducts(true);
    setSelectedPolicyProducts([]);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      let allProducts: any[] = [];

      const policyResponse = await axios.get(
        `${API_URL}/api/policies/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const policyData = policyResponse.data.data || policyResponse.data;
      console.log('📦 Policy data:', policyData);
      
      if (policyData.quote_id) {
        const quoteId = policyData.quote_id.toString();
        console.log(`📦 Fetching products from quote: ${quoteId}`);
        
        const endpoints = [
          { name: 'properties', url: `${API_URL}/api/properties?quote_id=${quoteId}` },
          { name: 'vehicles', url: `${API_URL}/api/vehicles?quote_id=${quoteId}` },
          { name: 'household-contents', url: `${API_URL}/api/household-contents?quote_id=${quoteId}` },
          { name: 'risk-items', url: `${API_URL}/api/risk-items?quote_id=${quoteId}` },
          { name: 'water-craft', url: `${API_URL}/api/water-craft?quote_id=${quoteId}` },
          { name: 'trailer-caravans', url: `${API_URL}/api/trailer-caravans?quote_id=${quoteId}` },
          { name: 'value-added', url: `${API_URL}/api/value-added?quote_id=${quoteId}` }
        ];

        for (const endpoint of endpoints) {
          try {
            const response = await axios.get(endpoint.url, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            let productData = [];
            if (Array.isArray(response.data)) {
              productData = response.data;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
              productData = response.data.data;
            }
            
            if (productData.length > 0) {
              console.log(`✅ Found ${productData.length} ${endpoint.name}`);
              productData.forEach((product: any) => {
                let productName = getProductDisplayName(product, endpoint.name);
                allProducts.push({
                  id: product.id,
                  product_id: product.id,
                  product_name: productName,
                  name: productName,
                  product_type: endpoint.name,
                  quote_id: quoteId,
                  uniqueKey: `${endpoint.name}-${product.id}`,
                  ...product
                });
              });
            }
          } catch (err: any) {
            if (err.response && err.response.status === 404) {
              console.log(`ℹ️ No ${endpoint.name} found`);
            } else {
              console.log(`⚠️ Error fetching ${endpoint.name}:`, err.message);
            }
          }
        }
        
        const seen = new Set();
        const uniqueProducts = allProducts.filter((product) => {
          const key = product.uniqueKey || `${product.product_type}-${product.id}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
        
        console.log(`📊 Total products found: ${uniqueProducts.length}`);
        setSelectedPolicyProducts(uniqueProducts);
        setIsLoadingProducts(false);
        return;
        
      } else {
        console.log('🔄 No quote_id, fetching products from policy tables and localStorage');
        
        let localProducts: any[] = [];

        try {
          const storageKey = `policy_${policyId}_value_added`;
          const storedProducts = localStorage.getItem(storageKey);
          
          if (storedProducts) {
            const draftProducts = JSON.parse(storedProducts);
            console.log(`📦 Found ${draftProducts.length} draft value-added products in localStorage for policy ${policyId}`);
            
            draftProducts.forEach((product: any) => {
              let productName = product.description || product.productType || product.name || `Value Added ${product.id}`;
              const productId = product.id || product.product_id || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
              localProducts.push({
                id: productId,
                product_id: productId,
                product_name: productName,
                name: productName,
                product_type: 'value-added',
                uniqueKey: `value-added-${productId}`,
                isDraft: true,
                ...product
              });
              console.log(`📦 Added draft product: ${productName} (${productId})`);
            });
          }
        } catch (err) {
          console.log('⚠️ Error reading localStorage:', err);
        }

        try {
          const allVaUrl = `${API_URL}/api/value-added?client_id=${clientId}`;
          console.log(`🔍 Fetching all value-added products for client`);
          
          const allVaResponse = await axios.get(allVaUrl, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          let allVaProducts = [];
          if (Array.isArray(allVaResponse.data)) {
            allVaProducts = allVaResponse.data;
          } else if (allVaResponse.data && allVaResponse.data.data && Array.isArray(allVaResponse.data.data)) {
            allVaProducts = allVaResponse.data.data;
          }
          
          const filteredVaProducts = allVaProducts.filter((p: any) => p.policy_id && p.policy_id.toString() === policyId);
          
          if (filteredVaProducts.length > 0) {
            console.log(`✅ Found ${filteredVaProducts.length} value-added products with policy_id ${policyId}`);
            filteredVaProducts.forEach((product: any) => {
              const exists = localProducts.some(p => p.id === product.id && p.product_type === 'value-added');
              if (!exists) {
                let productName = product.description || product.name || product.product_type || `Value Added ${product.id}`;
                localProducts.push({
                  id: product.id,
                  product_id: product.id,
                  product_name: productName,
                  name: productName,
                  product_type: 'value-added',
                  uniqueKey: `value-added-${product.id}`,
                  ...product
                });
              }
            });
          }
        } catch (err: any) {
          console.log('⚠️ Error fetching value-added products:', err.message);
        }

        try {
          const productsUrl = `${API_URL}/api/policy-products/${policyId}`;
          console.log(`🔍 Fetching junction table products from: ${productsUrl}`);
          
          const response = await axios.get(productsUrl, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          let productData = [];
          if (Array.isArray(response.data)) {
            productData = response.data;
          } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            productData = response.data.data;
          }
          
          if (productData.length > 0) {
            console.log(`✅ Found ${productData.length} products from junction tables for policy ${policyId}`);
            productData.forEach((product: any) => {
              const productType = product.product_type || 'product';
              let productName = getProductDisplayName(product, productType);
              const exists = localProducts.some(p => p.id === product.id && p.product_type === productType);
              if (!exists) {
                localProducts.push({
                  id: product.id,
                  product_id: product.id,
                  product_name: productName,
                  name: productName,
                  product_type: productType,
                  uniqueKey: `${productType}-${product.id}`,
                  ...product
                });
              }
            });
          }
        } catch (err: any) {
          if (err.response && err.response.status === 404) {
            console.log(`ℹ️ No junction table products found for policy ${policyId}`);
          } else {
            console.log(`⚠️ Error fetching junction products:`, err.message);
          }
        }

        const seen = new Set();
        const uniqueProducts = localProducts.filter((product) => {
          const key = product.uniqueKey || `${product.product_type}-${product.id}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
        
        console.log(`📊 Total products found: ${uniqueProducts.length}`);
        console.log('📊 Product names:', uniqueProducts.map(p => p.product_name));
        setSelectedPolicyProducts(uniqueProducts);
        setIsLoadingProducts(false);
        return;
      }
      
    } catch (error) {
      console.error('Error fetching policy products:', error);
      setSelectedPolicyProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const getProductDisplayName = (product: any, type: string): string => {
    let productName = '';
    
    if (type === 'properties') {
      const address = product.address_line_1 || product.addressLine1 || '';
      const propertyType = product.property_type || product.propertyType || '';
      productName = propertyType && address ? `${propertyType} - ${address}` : propertyType || address || `Property ${product.id}`;
    } else if (type === 'household-contents') {
      const description = product.description || '';
      const address = product.address_line_1 || product.addressLine1 || '';
      productName = description || `Household Contents - ${address}` || `Household Contents ${product.id}`;
    } else if (type === 'vehicles') {
      const make = product.make || '';
      const model = product.model || '';
      productName = make && model ? `${make} ${model}` : `Vehicle ${product.id}`;
    } else if (type === 'trailer-caravans') {
      productName = product.description || product.type || `Trailer Caravan ${product.id}`;
    } else if (type === 'risk-items') {
      productName = product.description || product.itemName || product.item_name || `Risk Item ${product.id}`;
    } else if (type === 'value-added') {
      productName = product.description || product.name || `Value Added ${product.id}`;
    } else if (type === 'water-craft') {
      productName = product.vesselName || product.vessel_name || product.description || `Water Craft ${product.id}`;
    }
    
    return productName.trim() || `${type} ${product.id}`;
  };

  const fetchProducts = async (clientId: string) => {
    setIsLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      let allProducts: any[] = [];
      let quoteId: string | null = null;
      let numericQuoteId: string | null = null;
      
      try {
        const policiesResponse = await axios.get(
          `${API_URL}/api/claims/policies?clientId=${clientId}&status=active`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (Array.isArray(policiesResponse.data)) {
          let quoteCandidates: { [key: string]: { count: number, policy: any } } = {};
          
          for (const policy of policiesResponse.data) {
            const policyNumber = policy.policy_number || '';
            const match = policyNumber.match(/^(Q-\d+-[A-Z0-9]+-[A-Z0-9]+)/);
            if (match) {
              const foundQuoteId = match[1];
              if (!quoteCandidates[foundQuoteId]) {
                quoteCandidates[foundQuoteId] = { count: 0, policy: policy };
              }
              quoteCandidates[foundQuoteId].count++;
            }
          }
          
          let maxCount = 0;
          let bestQuoteId = null;
          for (const [id, data] of Object.entries(quoteCandidates)) {
            if (data.count > maxCount) {
              maxCount = data.count;
              bestQuoteId = id;
            }
          }
          
          if (bestQuoteId) {
            quoteId = bestQuoteId;
            console.log('✅ Found main quote ID:', quoteId);
          }
        }
      } catch (err) {
        console.log('Could not get policies:', err);
      }

      console.log('🔍 Quote ID for filtering:', quoteId);

      const productEndpoints = [
        { url: `${API_URL}/api/properties?client_id=${clientId}`, name: 'Property', key: 'properties' },
        { url: `${API_URL}/api/household-contents?client_id=${clientId}`, name: 'Household Contents', key: 'householdContents' },
        { url: `${API_URL}/api/vehicles?client_id=${clientId}`, name: 'Vehicle', key: 'vehicles' },
        { url: `${API_URL}/api/trailer-caravans?client_id=${clientId}`, name: 'Trailer Caravan', key: 'trailerCaravans' },
        { url: `${API_URL}/api/risk-items?client_id=${clientId}`, name: 'Risk Item', key: 'riskItems' },
        { url: `${API_URL}/api/value-added?client_id=${clientId}`, name: 'Value Added', key: 'valueAdded' },
        { url: `${API_URL}/api/water-craft?client_id=${clientId}`, name: 'Water Craft', key: 'waterCraft' }
      ];
      
      let allRawProducts: any[] = [];
      
      for (const endpoint of productEndpoints) {
        try {
          const response = await axios.get(endpoint.url, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          let productData = [];
          if (Array.isArray(response.data)) {
            productData = response.data;
          } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            productData = response.data.data;
          }
          
          if (productData.length > 0) {
            productData.forEach((product: any) => {
              allRawProducts.push({
                ...product,
                _endpointKey: endpoint.key,
                _endpointName: endpoint.name
              });
            });
          }
        } catch (err) {
          console.log(`No products found at ${endpoint.url}`);
        }
      }

      if (allRawProducts.length > 0 && quoteId) {
        const matchingProducts = allRawProducts.filter((product: any) => {
          const policyNumber = product.policy_number || '';
          return policyNumber.includes(quoteId);
        });
        
        if (matchingProducts.length > 0) {
          const firstMatch = matchingProducts[0];
          numericQuoteId = firstMatch.quote_id || firstMatch.quoteId || '';
          console.log('✅ Numeric quote ID from matching product:', numericQuoteId);
        }
        
        if (!numericQuoteId) {
          const productWithQuoteId = allRawProducts.find((product: any) => {
            return product.quote_id || product.quoteId;
          });
          if (productWithQuoteId) {
            numericQuoteId = productWithQuoteId.quote_id || productWithQuoteId.quoteId || '';
            console.log('✅ Numeric quote ID from any product:', numericQuoteId);
          }
        }
        
        if (numericQuoteId) {
          const filteredByNumericId = allRawProducts.filter((product: any) => {
            const productQuoteId = product.quote_id || product.quoteId || '';
            return productQuoteId && numericQuoteId && productQuoteId.toString() === numericQuoteId.toString();
          });
          
          if (filteredByNumericId.length > 0) {
            allRawProducts = filteredByNumericId;
            console.log(`✅ Found ${filteredByNumericId.length} products with numeric quote ID ${numericQuoteId}`);
          } else {
            allRawProducts = allRawProducts.filter((product: any) => {
              const policyNumber = product.policy_number || '';
              return policyNumber.includes(quoteId);
            });
            console.log(`✅ Found ${allRawProducts.length} products with policy_number containing ${quoteId}`);
          }
        } else {
          allRawProducts = allRawProducts.filter((product: any) => {
            const policyNumber = product.policy_number || '';
            return policyNumber.includes(quoteId);
          });
          console.log(`✅ Found ${allRawProducts.length} products with policy_number containing ${quoteId}`);
        }
      }

      allRawProducts.forEach((product: any) => {
        const endpointKey = product._endpointKey;
        const endpointName = product._endpointName;
        
        let productName = '';
        
        if (endpointKey === 'properties') {
          const address = product.address_line_1 || product.addressLine1 || '';
          const suburb = product.suburb || '';
          const city = product.city || '';
          const propertyType = product.property_type || product.propertyType || '';
          
          if (propertyType && address) {
            productName = `${propertyType} - ${address}`;
          } else if (propertyType) {
            productName = propertyType;
          } else if (address) {
            productName = address;
          } else if (suburb && city) {
            productName = `${suburb}, ${city}`;
          } else {
            productName = `Property ${product.id}`;
          }
        } else if (endpointKey === 'householdContents') {
          const description = product.description || '';
          const address = product.address_line_1 || product.addressLine1 || '';
          
          if (description) {
            productName = description;
          } else if (address) {
            productName = `Household Contents - ${address}`;
          } else {
            productName = `Household Contents ${product.id}`;
          }
        } else if (endpointKey === 'vehicles') {
          const make = product.make || '';
          const model = product.model || '';
          const registration = product.registration || product.registration_number || '';
          
          if (make && model) {
            productName = `${make} ${model}`;
          } else if (make) {
            productName = make;
          } else if (registration) {
            productName = registration;
          } else {
            productName = `Vehicle ${product.id}`;
          }
        } else if (endpointKey === 'trailerCaravans') {
          const description = product.description || '';
          const type = product.type || product.vehicle_type || '';
          
          if (description) {
            productName = description;
          } else if (type) {
            productName = type;
          } else {
            productName = `Trailer Caravan ${product.id}`;
          }
        } else if (endpointKey === 'riskItems') {
          const description = product.description || '';
          const itemName = product.itemName || product.item_name || '';
          
          if (description) {
            productName = description;
          } else if (itemName) {
            productName = itemName;
          } else {
            productName = `Risk Item ${product.id}`;
          }
        } else if (endpointKey === 'valueAdded') {
          const description = product.description || '';
          const name = product.name || '';
          
          if (description) {
            productName = description;
          } else if (name) {
            productName = name;
          } else {
            productName = `Value Added ${product.id}`;
          }
        } else if (endpointKey === 'waterCraft') {
          const vesselName = product.vesselName || product.vessel_name || '';
          const description = product.description || '';
          
          if (vesselName) {
            productName = vesselName;
          } else if (description) {
            productName = description;
          } else {
            productName = `Water Craft ${product.id}`;
          }
        }
        
        productName = productName.trim() || `${endpointName} ${product.id}`;
        
        const uniqueKey = `${endpointKey}-${product.id}`;
        
        allProducts.push({
          id: product.id,
          product_id: product.id,
          product_name: productName,
          name: productName,
          product_type: endpointName,
          quote_id: quoteId,
          numeric_quote_id: numericQuoteId,
          uniqueKey: uniqueKey,
          ...product
        });
      });

      const seen = new Set();
      const uniqueProducts = allProducts.filter((product) => {
        const key = product.uniqueKey || `${product.product_type}-${product.id}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
      
      console.log(`📊 Products for quote ${quoteId}:`, uniqueProducts.length);
      console.log('📊 Product names:', uniqueProducts.map(p => p.product_name));
      setProducts(uniqueProducts);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const fetchClientDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
  `${API_URL}/api/clients/${clientId}`,
  { headers: { Authorization: `Bearer ${token}` } }
);
      
      setSelectedClient(response.data);
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

  const fetchEmployees = async () => {
  setIsLoadingEmployees(true);
  setError('');
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      throw new Error('No authentication token found');
    }

    console.log('🔍 Fetching consultants from:', `${API_URL}/api/consultants`);
    
    const response = await axios.get(
      `${API_URL}/api/consultants`,
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } 
      }
    );
    
    console.log('📥 Full API response:', response);
    console.log('📥 Response data:', response.data);
    
    let consultantsData: any[] = [];
    
    // Handle different response formats
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      consultantsData = response.data.data;
      console.log('✅ Found consultants in response.data.data:', consultantsData.length);
    } else if (Array.isArray(response.data)) {
      consultantsData = response.data;
      console.log('✅ Found consultants in response.data (array):', consultantsData.length);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      consultantsData = response.data.data;
      console.log('✅ Found consultants in response.data.data (nested):', consultantsData.length);
    } else {
      console.warn('⚠️ Unexpected response format:', response.data);
      consultantsData = [];
    }
    
    console.log('📋 Raw consultants data:', consultantsData);
    
    // Filter active consultants and ensure IDs are numbers
    const activeConsultants = consultantsData
      .filter((c: any) => c.active !== false)
      .map((c: any) => ({
        id: Number(c.id),
        name: c.name || '',
        surname: c.surname || '',
        email: c.email || '',
        role: c.role || 'consultant'
      }));
    
    console.log('✅ Active consultants loaded:', activeConsultants.length);
    console.log('📋 Active consultants:', activeConsultants);
    
    setEmployees(activeConsultants);
    
  } catch (error: any) {
    console.error('❌ Error fetching consultants:', error);
    console.error('Error details:', error.response?.data);
    setEmployees([]);
    setError('Failed to load consultants: ' + (error.response?.data?.message || error.message));
  } finally {
    setIsLoadingEmployees(false);
  }
};

  const handleOpenModal = () => {
  const today = new Date().toISOString().split('T')[0];
  setFormData({
    policyId: '',
    productId: '',
    claimMethod: '',
    claimType: '',
    description: '',
    assignedTo: '',
    claimsReportedDate: today,
    claimDate: today,
    underwritersClaimNumber: '',
    totalCostOfClaim: '',
    claimExcess: '',
  });
  setSelectedPolicyProducts([]);
  setError('');
  fetchEmployees();
  setIsModalOpen(true);
};

  const handleCloseModal = () => {
  setIsModalOpen(false);
  setError('');
  setFormData({
    policyId: '',
    productId: '',
    claimMethod: '',
    claimType: '',
    description: '',
    assignedTo: '',
    claimsReportedDate: '',
    claimDate: '',
    underwritersClaimNumber: '',
    totalCostOfClaim: '',
    claimExcess: '',
  });
  setSelectedPolicyProducts([]);
};

  const handleReferenceClick = async (claimId: string) => {
  setSelectedClaimId(claimId);
  
  // Fetch consultants FIRST
  console.log('🔍 Fetching consultants before showing detail...');
  await fetchEmployees();
  
  const claim = claims.find(r => r.id.toString() === claimId);
  if (claim) {
    setSelectedClaim({
      ...claim,
      date_received: claim.date_received || '',
      date_sign_off: claim.date_sign_off || '',
      client_id: parseInt(clientId),
      originalDescription: claim.description,
      policy_id: claim.policy_id,
      policy_number: claim.policy_number,
      product_id: claim.product_id,
      product_name: claim.product_name,
      claims_reported_date: claim.claims_reported_date || '',
      underwriters_claim_number: claim.underwriters_claim_number || '',
      total_cost_of_claim: claim.total_cost_of_claim || 0,
      claim_excess: claim.claim_excess || 0,
            consultant_id: claim.consultant_id,
    } as ClaimDetail);
  }
  setView('detail');
  fetchPolicies(clientId);
  if (claimId) {
    fetchClaimDetail(claimId);
  }
};

  const handleBackToList = () => {
    setView('list');
    setSelectedClaimId(null);
    setSelectedClaim(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.claimMethod || !formData.claimType || !formData.description) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const productId = formData.productId ? parseInt(formData.productId) : null;
      const totalCost = formData.totalCostOfClaim ? parseFloat(formData.totalCostOfClaim) : null;
      const claimExcess = formData.claimExcess ? parseFloat(formData.claimExcess) : null;
      
      console.log('📝 Creating claim with:', {
        client_id: parseInt(clientId),
        policy_id: formData.policyId ? parseInt(formData.policyId) : null,
        product_id: productId,
        claim_method: formData.claimMethod,
        claim_type: formData.claimType,
        description: formData.description,
          consultant_id: formData.assignedTo ? parseInt(formData.assignedTo) : null,
        claims_reported_date: formData.claimsReportedDate || null,
        underwriters_claim_number: formData.underwritersClaimNumber || null,
        total_cost_of_claim: totalCost,
        claim_excess: claimExcess,
      });

      const payload = {
  client_id: parseInt(clientId),
  policy_id: formData.policyId ? parseInt(formData.policyId) : null,
  product_id: productId,
  claim_method: formData.claimMethod,
  claim_type: formData.claimType,
  description: formData.description,
    consultant_id: formData.assignedTo ? parseInt(formData.assignedTo) : null,
  status: 'pending',
  date_received: formData.claimDate || new Date().toISOString().split('T')[0],
  claims_reported_date: formData.claimsReportedDate || null,
  underwriters_claim_number: formData.underwritersClaimNumber || null,
  total_cost_of_claim: totalCost,
  claim_excess: claimExcess,
};

      const response = await axios.post(
        `${API_URL}/api/claims`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ Claim created with product_id:', productId);
      console.log('📦 Response:', response.data);

      setSuccess('Claim created successfully!');
      handleCloseModal();
      setFormData({
  policyId: '',
  productId: '',
  claimMethod: '',
  claimType: '',
  description: '',
  assignedTo: '',
  claimsReportedDate: '',
  claimDate: '',
  underwritersClaimNumber: '',
  totalCostOfClaim: '',
  claimExcess: '',
});
      await fetchClaims();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error: any) {
      console.error('Error creating claim:', error);
      setError(error.response?.data?.message || 'Failed to create claim');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selectedClaim || selectedClaim.status === newStatus) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSelectedClaim({
          ...selectedClaim,
          status: newStatus
        });
        setSuccess('Claim status updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      setError(error.response?.data?.message || 'Failed to update claim status');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignedOff = async () => {
  if (!selectedClaim) return;
  
  setIsSaving(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const today = new Date().toISOString().split('T')[0];
    
    const storedUser = localStorage.getItem('user');
    let signedOffBy = '';
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const name = user.username || user.email || 'System';
        const surname = user.surname || '';
        signedOffBy = `${name} ${surname}`.trim() || name || 'System';
      } catch (e) {
        signedOffBy = 'System';
      }
    }
    
    const response = await axios.put(
      `${API_URL}/api/claims/${selectedClaim.id}`,
      { 
        date_sign_off: today,
        status: 'completed',
        signed_off_by: signedOffBy
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Claim updated successfully' || response.status === 200) {
      setSuccess('Claim signed off successfully');
      
      // Navigate back to list view after successful sign-off
      setView('list');
      setSelectedClaimId(null);
      setSelectedClaim(null);
      
      // Refresh the claims list
      await fetchClaims();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    }
  } catch (error: any) {
    console.error('Error signing off claim:', error);
    setError(error.response?.data?.message || 'Failed to sign off claim');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSaving(false);
  }
};

  const handleClaimMethodChange = async (newMethod: string) => {
    if (!selectedClaim || selectedClaim.claim_method === newMethod) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { claim_method: newMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSelectedClaim({
          ...selectedClaim,
          claim_method: newMethod
        });
        setSuccess('Method updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating method:', error);
      setError(error.response?.data?.message || 'Failed to update method');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClaimTypeChange = async (newType: string) => {
    if (!selectedClaim || selectedClaim.claim_type === newType) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { claim_type: newType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSelectedClaim({
          ...selectedClaim,
          claim_type: newType
        });
        setSuccess('Claim type updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating claim type:', error);
      setError(error.response?.data?.message || 'Failed to update claim type');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConsultantAssignment = async (consultantId: string) => {
  if (!selectedClaim) return;
  
  setIsSaving(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const assignedToId = consultantId ? parseInt(consultantId) : null;
    
        const response = await axios.put(
      `${API_URL}/api/claims/${selectedClaim.id}`,
      { consultant_id: assignedToId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Claim updated successfully' || response.status === 200) {
      // Find the consultant name
      const consultant = employees.find(c => c.id === assignedToId);
      const consultantName = consultant ? `${consultant.name} ${consultant.surname}` : 'Unassigned';
      
      setSelectedClaim({
        ...selectedClaim,
        consultant_id: assignedToId || undefined,
        consultant_name: consultantName
      });
      setSuccess('Consultant assigned successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchClaims();
    }
  } catch (error: any) {
    console.error('Error assigning consultant:', error);
    setError(error.response?.data?.message || 'Failed to assign consultant');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSaving(false);
  }
};

  const handleDescriptionUpdate = async () => {
    if (!selectedClaim) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { description: selectedClaim.description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSuccess('Description updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
        setSelectedClaim({
          ...selectedClaim,
          originalDescription: selectedClaim.description
        });
      }
    } catch (error: any) {
      console.error('Error updating description:', error);
      setError(error.response?.data?.message || 'Failed to update description');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePolicyChange = async (newPolicyId: string) => {
    if (!selectedClaim) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const policyId = newPolicyId ? parseInt(newPolicyId) : null;
      const selectedPolicy = policies.find(p => p.id === policyId);
      
      if (policyId) {
        await fetchPolicyProducts(policyId.toString());
      } else {
        setSelectedPolicyProducts([]);
      }
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { policy_id: policyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        const currentProducts = selectedPolicyProducts;
        
        let selectedProductId = selectedClaim.product_id;
        if (selectedProductId && !currentProducts.some(p => Number(p.id) === Number(selectedProductId))) {
          selectedProductId = undefined;
        }
        
        setSelectedClaim({
          ...selectedClaim,
          policy_id: policyId || undefined,
          policy_number: selectedPolicy?.policy_number || 'No policy selected',
          product_id: selectedProductId,
          product_name: selectedProductId ? 
            currentProducts.find(p => Number(p.id) === Number(selectedProductId))?.product_name || 'No product selected' :
            'No product selected'
        });
        
        setSuccess('Policy updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating policy:', error);
      setError(error.response?.data?.message || 'Failed to update policy');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleProductChange = async (newProductId: string) => {
    console.log('🔄 handleProductChange called with:', { newProductId, selectedPolicyProducts });
    if (!selectedClaim) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const productId = newProductId ? parseInt(newProductId) : null;
      const selectedProduct = selectedPolicyProducts.find(p => p.id === productId);
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { product_id: productId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSelectedClaim({
          ...selectedClaim,
          product_id: productId || undefined,
          product_name: selectedProduct?.product_name || selectedProduct?.name || 'No product selected'
        });
        setSuccess('Product updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating product:', error);
      setError(error.response?.data?.message || 'Failed to update product');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnderwritersClaimNumberChange = async (value: string) => {
    if (!selectedClaim) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { underwriters_claim_number: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSelectedClaim({
          ...selectedClaim,
          underwriters_claim_number: value
        });
        setSuccess('Underwriters claim number updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating underwriters claim number:', error);
      setError(error.response?.data?.message || 'Failed to update underwriters claim number');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTotalCostOfClaimChange = async (value: string) => {
    if (!selectedClaim) return;
    
    const numericValue = value ? parseFloat(value) : null;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { total_cost_of_claim: numericValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSelectedClaim({
          ...selectedClaim,
          total_cost_of_claim: numericValue || 0
        });
        setSuccess('Total cost of claim updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating total cost of claim:', error);
      setError(error.response?.data?.message || 'Failed to update total cost of claim');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClaimExcessChange = async (value: string) => {
    if (!selectedClaim) return;
    
    const numericValue = value ? parseFloat(value) : null;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/claims/${selectedClaim.id}`,
        { claim_excess: numericValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Claim updated successfully' || response.status === 200) {
        setSelectedClaim({
          ...selectedClaim,
          claim_excess: numericValue || 0
        });
        setSuccess('Claim excess updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClaims();
      }
    } catch (error: any) {
      console.error('Error updating claim excess:', error);
      setError(error.response?.data?.message || 'Failed to update claim excess');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewProductDetails = (product: any, productType: string) => {
    console.log('🔍 Viewing product details:', { product, productType });
    
    if (!product) {
      console.error('❌ No product data to display');
      return;
    }
    
    const productData = {
      product: {
        ...product,
        type: productType,
        name: product.product_name || product.name || 'Product',
        value: product.value || product.insured_value || product.insuredValue || 0,
        description: product.description || product.product_name || product.name || '',
        data: { ...product }
      },
      quote: {
        id: selectedClaim?.policy_id || 'N/A',
        referenceNumber: selectedClaim?.policy_number || 'N/A'
      }
    };
    
    console.log('📦 Setting product to view:', productData);
    setProductToView(productData);
    setTimeout(() => {
      setIsViewProductModalVisible(true);
    }, 0);
  };

  const formatDate = (dateString: string): string => {
  if (!dateString) return 'Not set';
  // Parse the date as local date to avoid timezone issues
  const parts = dateString.split('T')[0].split('-');
  if (parts.length === 3) {
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  return new Date(dateString).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

  const formatLabel = (value: string): string => {
    if (!value) return 'Open';
    return value.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getProductTypeDisplay = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'properties': 'Property',
      'property': 'Property',
      'vehicles': 'Vehicle',
      'vehicle': 'Vehicle',
      'household-contents': 'Household Contents',
      'household': 'Household Contents',
      'risk-items': 'Risk Item',
      'risk-item': 'Risk Item',
      'risk_item': 'Risk Item',
      'water-craft': 'Water Craft',
      'watercraft': 'Water Craft',
      'trailer-caravans': 'Trailer/Caravan',
      'trailer': 'Trailer/Caravan',
      'value-added': 'Value Added',
      'value_added': 'Value Added'
    };
    return typeMap[type] || type;
  };

  const formatCurrency = (value: any): string => {
    if (!value && value !== 0) return 'Not set';
    try {
      const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
      return isNaN(numValue) ? 'Not set' : `R ${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    } catch {
      return 'Not set';
    }
  };

  // Claims Section Component
  const ClaimSection: React.FC<{
    title: string;
    claims: Claim[];
    onReferenceClick: (id: string) => void;
    formatDate: (date: string) => string;
    formatLabel: (value: string) => string;
  }> = ({ title, claims, onReferenceClick, formatDate, formatLabel }) => {
    if (claims.length === 0) {
      return (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">{title} ({claims.length})</h3>
          <p className="text-gray-500 italic">No {title.toLowerCase()}</p>
        </div>
      );
    }

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-3 text-gray-800">{title} ({claims.length})</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Claim No</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Description</th>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Consultant</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Created</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Last Updated</th>
                <th className="px-4 py-2 text-center text-sm font-medium text-gray-600 border-b">Status</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => (
                <tr key={claim.id} className="hover:bg-gray-50 border-b border-gray-100">
                  <td className="px-4 py-2 text-sm">
                    <button
                      onClick={() => onReferenceClick(claim.id.toString())}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      {claim.reference_number}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-800 max-w-xs truncate">
                    {claim.description}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {claim.consultant_name}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {formatDate(claim.created_date)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {formatDate(claim.date_sign_off || claim.created_date)}
                  </td>
                  <td className="px-4 py-2 text-sm text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        claim.status?.toLowerCase() === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : claim.status?.toLowerCase() === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                        {claimStatuses.find(s => s.value === claim.status)?.label || formatLabel(claim.status || 'Open')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============================================
  // DETAIL VIEW - Conditional Return
  // ============================================
  if (view === 'detail' && selectedClaim) {
    const isSignedOff = !!selectedClaim.date_sign_off;
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Claim Details</h3>
          <div className="flex gap-2">
            {!isSignedOff && selectedClaim.status?.toLowerCase() === 'completed' && (
              <button
                onClick={handleSignedOff}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Signed Off
              </button>
            )}
            <button
              onClick={handleBackToList}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to List
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Claim Details
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'whatsapp'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              WhatsApp
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Email
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === 'details' && (
            <div className="bg-white shadow-md rounded-lg p-6 border">
              <div className="grid grid-cols-2 gap-6">
                {/* Reference Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                    {selectedClaim.reference_number}
                  </div>
                </div>

                {/* Claim Type - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Claim Type</label>
                  <select
                    value={selectedClaim.claim_type}
                    onChange={(e) => handleClaimTypeChange(e.target.value)}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {claimTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                </div>

                {/* Claim Type - Dropdown */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Claim Type</label>
  <select
    value={selectedClaim.claim_type}
    onChange={(e) => handleClaimTypeChange(e.target.value)}
    disabled={isSaving || isSignedOff}
    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      isSignedOff 
        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
        : 'border-gray-300 bg-white'
    }`}
  >
    {claimTypes.map((type) => (
      <option key={type.value} value={type.value}>
        {type.label}
      </option>
    ))}
  </select>
  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
</div>

{/* Assign to Consultant - NEW FIELD */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Consultant</label>
    <select
    value={selectedClaim.consultant_id?.toString() || ''}
    onChange={(e) => handleConsultantAssignment(e.target.value)}
    disabled={isSaving || isSignedOff || isLoadingEmployees}
    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      isSignedOff 
        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
        : 'border-gray-300 bg-white'
    }`}
  >
    <option value="">Unassigned</option>
    {employees.map((consultant) => (
      <option key={consultant.id} value={consultant.id.toString()}>
        {consultant.name} {consultant.surname}
      </option>
    ))}
  </select>
  {isLoadingEmployees && (
    <p className="text-xs text-blue-600 mt-1">Loading consultants...</p>
  )}
  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
</div>

                {/* Description - Editable */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="flex gap-2">
                    <textarea
                      value={selectedClaim.description}
                      onChange={(e) => {
                        setSelectedClaim({
                          ...selectedClaim,
                          description: e.target.value
                        });
                      }}
                      disabled={isSaving || isSignedOff}
                      rows={3}
                      className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isSignedOff 
                          ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                          : 'border-gray-300 bg-white'
                      }`}
                      placeholder="Enter description..."
                    />
                    {selectedClaim.description !== selectedClaim.originalDescription && !isSignedOff && (
                      <button
                        onClick={handleDescriptionUpdate}
                        disabled={isSaving}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save
                      </button>
                    )}
                  </div>
                  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                </div>

                {/* Method Received - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Method Received</label>
                  <select
                    value={selectedClaim.claim_method}
                    onChange={(e) => handleClaimMethodChange(e.target.value)}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {claimMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                </div>

                {/* Policy Number - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
                  <select
                    value={selectedClaim.policy_id ? selectedClaim.policy_id.toString() : ''}
                    onChange={(e) => handlePolicyChange(e.target.value)}
                    disabled={isSaving || isLoadingPolicies || isSignedOff}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    <option value="">No policy selected</option>
                    {policies.map((policy) => (
                      <option key={policy.id} value={policy.id.toString()}>
                        {policy.policy_number}
                      </option>
                    ))}
                  </select>
                  {isLoadingPolicies && <p className="text-xs text-blue-600 mt-1">Loading policies...</p>}
                  {policies.length === 0 && !isLoadingPolicies && (
                    <p className="text-xs text-gray-500 mt-1">No active policies available for this client</p>
                  )}
                </div>

                {/* Status - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Claim Status</label>
                  <select
                    value={selectedClaim.status?.toLowerCase() || ''}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {claimStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                </div>

                {/* Date Received - Display Only */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Claim Date</label>
  <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
    {formatDate(selectedClaim.date_received)}
  </div>
</div>

                {/* Claims Reported Date - Display Only */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Claims Reported Date</label>
  <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
    {formatDate(selectedClaim.claims_reported_date || '')}
  </div>
</div>

                {/* Underwriters Claim Number - Details View Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Underwriters Claim Number</label>
                  <input
                    type="text"
                    value={selectedClaim?.underwriters_claim_number || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedClaim(prev => prev ? { ...prev, underwriters_claim_number: value } : null);
                      handleUnderwritersClaimNumberChange(value);
                    }}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Enter underwriters claim number..."
                  />
                  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                </div>

                {/* Total Cost of Claim - Details View Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost of Claim</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="number"
                      value={selectedClaim?.total_cost_of_claim || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedClaim(prev => prev ? { ...prev, total_cost_of_claim: value ? parseFloat(value) : undefined } : null);
                        handleTotalCostOfClaimChange(value);
                      }}
                      disabled={isSaving || isSignedOff}
                      className={`w-full px-3 py-2 pl-8 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isSignedOff 
                          ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                          : 'border-gray-300 bg-white'
                      }`}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                </div>

                {/* Claim Excess - Details View Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Claim Excess</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="number"
                      value={selectedClaim?.claim_excess || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedClaim(prev => prev ? { ...prev, claim_excess: value ? parseFloat(value) : undefined } : null);
                        handleClaimExcessChange(value);
                      }}
                      disabled={isSaving || isSignedOff}
                      className={`w-full px-3 py-2 pl-8 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isSignedOff 
                          ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                          : 'border-gray-300 bg-white'
                      }`}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  {isSaving && <p className="text-xs text-blue-600 mt-1">Saving...</p>}
                </div>

                {/* Date Sign Off and Signed Off By - Side by side */}
                {selectedClaim.date_sign_off && (
                  <div className="col-span-2">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Sign Off</label>
                        <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                          {formatDate(selectedClaim.date_sign_off)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Signed Off By</label>
                        <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                          {selectedClaim.signed_off_by || signedOffByName || 'System'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Product Summary Card - Show ONLY the selected product */}
              {selectedClaim?.product_id ? (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 text-sm">Selected Product</h5>
                        <div className="mt-2">
                          {(() => {
                            const selectedProduct = selectedPolicyProducts.find((p: any) => 
                              String(p.id) === String(selectedClaim.product_id) || 
                              String(p.product_id) === String(selectedClaim.product_id)
                            );
                            
                            const productName = selectedProduct?.product_name || selectedProduct?.name || selectedClaim.product_name || 'Product';
                            const productType = selectedProduct?.product_type || 'Product';
                            
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  console.log('🖱️ Product clicked!', { selectedProduct, selectedClaim });
                                  handleViewProductDetails(selectedProduct || selectedClaim, productType);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-100 border border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400 transition-colors cursor-pointer group w-full text-left"
                              >
                                <span className="font-medium">{productName}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded bg-blue-200 text-blue-700">{productType}</span>
                                <svg className="w-4 h-4 ml-auto text-blue-500 group-hover:text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Click on the product to view full details</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-700 text-sm">No Product Selected</h5>
                        <p className="text-sm text-gray-500 mt-1">No product has been selected for this claim yet.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Claim Questions Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <ClaimQuestions 
                  claimId={selectedClaim.id.toString()} 
                  clientId={clientId}
                  clientName={selectedClient?.name + ' ' + selectedClient?.surname || 'Client'}
                />
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="bg-white shadow-md rounded-lg p-6 border">
              <ClientClaimDocuments claimId={selectedClaim.id.toString()} clientId={clientId} />
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="bg-white shadow-md rounded-lg p-6 border">
              <ClientClaimNotes claimId={selectedClaim.id.toString()} />
            </div>
          )}

          {activeTab === 'whatsapp' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientClaimWhatsAppTab 
      clientId={clientId}
      claimId={selectedClaim.id.toString()}
      claimRef={selectedClaim.reference_number || `CLM-${selectedClaim.id}`}
      clientName={selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : ''}
      policyNumber={selectedClaim.policy_number || ''}
      user={(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            return JSON.parse(storedUser);
          } catch {
            return undefined;
          }
        }
        return undefined;
      })()}
    />
  </div>
)}

          {activeTab === 'email' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientClaimEmailTab 
      clientId={clientId}
      claimId={selectedClaim.id.toString()}
      claimRef={selectedClaim.reference_number || `CLM-${selectedClaim.id}`}
      clientName={selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : ''}
      policyNumber={selectedClaim.policy_number || ''}
      user={(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            return JSON.parse(storedUser);
          } catch {
            return undefined;
          }
        }
        return undefined;
      })()}
    />
  </div>
)}
        </div>

        {/* ✅ PRODUCT DETAILS MODAL - INSIDE DETAIL VIEW */}
        {isViewProductModalVisible && productToView && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-[9999]">
            <div className="relative bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-4 border-b">
                <h3 className="text-2xl font-bold text-gray-800">
                  {getProductTypeDisplay(productToView.product.type)} Details
                </h3>
                <button
                  onClick={() => {
                    setIsViewProductModalVisible(false);
                    setProductToView(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              {/* Product details content */}
              <div className="space-y-4">
                {productToView.product.type === 'properties' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.addressLine1 || productToView.product.data?.address_line_1 || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.addressLine2 || productToView.product.data?.address_line_2 || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.suburb || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.city || productToView.product.data?.city_town || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.postalCode || productToView.product.data?.postal_code || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Region</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.region || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Country</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.country || 'South Africa'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.propertyType || productToView.product.data?.property_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Residence Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.residenceType || productToView.product.data?.residence_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Building Age</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.buildingAge || productToView.product.data?.building_age || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                  </div>
                )}

                {productToView.product.type === 'vehicles' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vehicle_type || productToView.product.data?.vehicleType || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.registration_number || productToView.product.data?.registrationNumber || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Make</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.make || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Model</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.model || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.year || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Variant</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.variant || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Color</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.color || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Usage</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.usage || productToView.product.data?.vehicle_usage || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">VIN Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vin_number || productToView.product.data?.vinNumber || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Engine Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.engine_number || productToView.product.data?.engineNumber || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">MMCode</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.mmcode || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.cover_type || productToView.product.data?.coverType || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insured_value || productToView.product.data?.insuredValue || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                  </div>
                )}

                {productToView.product.type === 'trailer-caravans' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Trailer/Caravan Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vehicleType || productToView.product.data?.vehicle_type || productToView.product.data?.trailer_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.registrationNumber || productToView.product.data?.registration_number || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Make</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.make || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Model</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.model || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.year || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Use Retail Value</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.useRetailValue || productToView.product.data?.use_retail_value ? 'Yes' : 'No'}</p></div>
                  </div>
                )}

                {productToView.product.type === 'risk-items' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.itemType || productToView.product.data?.item_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.itemName || productToView.product.data?.item_name || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.serialNumber || productToView.product.data?.serial_number || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.description || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">No Claim Bonus</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.noClaimsBonus || productToView.product.data?.no_claims_bonus || 'N/A'}</p></div>
                  </div>
                )}

                {productToView.product.type === 'household-contents' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.addressLine1 || productToView.product.data?.address_line_1 || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.suburb || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">City/Town</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.cityTown || productToView.product.data?.city_town || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.propertyType || productToView.product.data?.property_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                  </div>
                )}

                {productToView.product.type === 'water-craft' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Vessel Name</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vesselName || productToView.product.data?.vessel_name || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Craft Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.craftType || productToView.product.data?.craft_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Hull Make</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.hullMake || productToView.product.data?.hull_make || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.year || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Total Sum Insured</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.totalSumInsured || productToView.product.data?.total_sum_insured || 0)}</p></div>
                  </div>
                )}

                {productToView.product.type === 'value-added' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.productType || productToView.product.data?.product_type || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.description || 'N/A'}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.value || 0)}</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                  </div>
                )}

                {!['properties', 'vehicles', 'trailer-caravans', 'risk-items', 'household-contents', 'water-craft', 'value-added'].includes(productToView.product.type) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Product Data</label>
                      <div className="p-4 bg-gray-50 rounded-lg border overflow-x-auto max-h-60 overflow-y-auto">
                        <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(productToView.product.data, null, 2)}</pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setIsViewProductModalVisible(false);
                    setProductToView(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // LIST VIEW - Main Return
  // ============================================
  const openClaims = claims.filter(r => r.status?.toLowerCase() !== 'completed' && r.status?.toLowerCase() !== 'cancelled');
  const completedClaims = claims.filter(r => r.status?.toLowerCase() === 'completed');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">Client Claims</h3>
          <p className="text-sm text-gray-500 mt-1">Manage client claims and track progress</p>
        </div>
        <button
  onClick={() => window.location.href = `/create-claim/${clientId}`}
  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
>
  <PlusIcon className="w-4 h-4 mr-2" />
  Create Claim
</button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-700 hover:text-red-900">×</button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {isLoadingClaims ? (
        <div className="text-center py-8 text-gray-500">Loading claims...</div>
      ) : (
        <>
          <ClaimSection 
            title="Open Claims" 
            claims={openClaims}
            onReferenceClick={handleReferenceClick}
            formatDate={formatDate}
            formatLabel={formatLabel}
          />
          <ClaimSection 
            title="Completed Claims" 
            claims={completedClaims}
            onReferenceClick={handleReferenceClick}
            formatDate={formatDate}
            formatLabel={formatLabel}
          />
        </>
      )}

      {/* Create Claim Modal */}
{isModalOpen && (
  <div className="fixed inset-0 z-[9999] overflow-y-auto overflow-x-visible">
    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>
      <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full overflow-visible">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
  <div className="sm:flex sm:items-start">
    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Create New Claim
                      </h3>
                      
                      {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                          {error}
                        </div>
                      )}

                      <div className="space-y-4">
                        {/* Select Policy */}
                        <div>
                          <label htmlFor="policy" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Policy <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <select
                            id="policy"
                            value={formData.policyId}
                            onChange={(e) => {
                              const selectedPolicyId = e.target.value;
                              setFormData(prev => ({ ...prev, policyId: selectedPolicyId, productId: '' }));
                              setSelectedPolicyProducts([]);
                              if (selectedPolicyId) {
                                fetchPolicyProducts(selectedPolicyId);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={isLoadingPolicies}
                          >
                            <option value="">No policy selected</option>
                            {policies.map(policy => (
                              <option key={policy.id} value={policy.id.toString()}>
                                {policy.policy_number}
                              </option>
                            ))}
                          </select>
                          {isLoadingPolicies && (
                            <p className="text-xs text-blue-600 mt-1">Loading policies...</p>
                          )}
                          {policies.length === 0 && !isLoadingPolicies && (
                            <p className="text-xs text-gray-500 mt-1">No active policies found for this client</p>
                          )}
                        </div>

                        {/* Select Product */}
                        <div>
                          <label htmlFor="product" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Product <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <select
                            id="product"
                            value={formData.productId}
                            onChange={(e) => {
                              const selectedProductId = e.target.value;
                              console.log('🔄 Product selected:', selectedProductId);
                              setFormData(prev => ({ ...prev, productId: selectedProductId }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={isLoadingProducts || !formData.policyId}
                          >
                            <option value="">
                              {!formData.policyId ? 'Please select a policy first' : 
                               isLoadingProducts ? 'Loading products...' : 
                               selectedPolicyProducts.length === 0 ? 'No products found' : 
                               'Select a product'}
                            </option>
                            {selectedPolicyProducts.map((product) => {
                              const productId = product.id || product.product_id;
                              const productName = product.product_name || product.name || product.product_type || `Product ${product.id}`;
                              return (
                                <option 
                                  key={product.uniqueKey || `product-${productId}-${product.product_type}`} 
                                  value={String(productId)}
                                >
                                  {productName}
                                </option>
                              );
                            })}
                          </select>
                          {isLoadingProducts && (
                            <p className="text-xs text-blue-600 mt-1">Loading products...</p>
                          )}
                          {selectedPolicyProducts.length === 0 && !isLoadingProducts && formData.policyId && (
                            <p className="text-xs text-gray-500 mt-1">No products found for this policy</p>
                          )}
                        </div>

                        {/* Select Claim Method */}
                        <div>
                          <label htmlFor="claimMethod" className="block text-sm font-medium text-gray-700 mb-1">
                            Method Received *
                          </label>
                          <select
                            id="claimMethod"
                            value={formData.claimMethod}
                            onChange={(e) => setFormData(prev => ({ ...prev, claimMethod: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Select method received</option>
                            {claimMethods.map(method => (
                              <option key={method.value} value={method.value}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Select Claim Type */}
                        <div>
                          <label htmlFor="claimType" className="block text-sm font-medium text-gray-700 mb-1">
                            Claim Type *
                          </label>
                          <select
                            id="claimType"
                            value={formData.claimType}
                            onChange={(e) => setFormData(prev => ({ ...prev, claimType: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Select claim type</option>
                            {claimTypes.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                                                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Consultant <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <select
                            id="assignedTo"
                            value={formData.assignedTo}
                            onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={isLoadingEmployees}
                          >
                                                        <option value="">Select consultant</option>
                            {employees.map((consultant: any) => (
                              <option key={consultant.id} value={consultant.id}>
                                {consultant.name} {consultant.surname}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                          </label>
                          <textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter claim description..."
                            required
                          />
                        </div>

                        {/* Claims Reported Date */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Claims Reported Date <span className="text-gray-400 text-xs">(optional)</span>
  </label>
  <DateInput
    value={formData.claimsReportedDate || ''}
    onChange={(date) => {
      setFormData(prev => ({ ...prev, claimsReportedDate: date }));
    }}
    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    placeholder="Select claims reported date"
  />
</div>

                        {/* Claim Date */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Claim Date <span className="text-gray-400 text-xs">(optional)</span>
  </label>
  <DateInput
    value={formData.claimDate || ''}
    onChange={(date) => {
      setFormData(prev => ({ ...prev, claimDate: date }));
    }}
    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    placeholder="Select claim date"
  />
</div>

                        {/* Underwriters Claim Number - Modal Field */}
                        <div>
                          <label htmlFor="underwritersClaimNumber" className="block text-sm font-medium text-gray-700 mb-1">
                            Underwriters Claim Number <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <input
                            type="text"
                            id="underwritersClaimNumber"
                            value={formData.underwritersClaimNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, underwritersClaimNumber: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter underwriters claim number..."
                          />
                        </div>

                        {/* Total Cost of Claim - Modal Field */}
                        <div>
                          <label htmlFor="totalCostOfClaim" className="block text-sm font-medium text-gray-700 mb-1">
                            Total Cost of Claim <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500">R</span>
                            </div>
                            <input
                              type="number"
                              id="totalCostOfClaim"
                              value={formData.totalCostOfClaim}
                              onChange={(e) => setFormData(prev => ({ ...prev, totalCostOfClaim: e.target.value }))}
                              className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>

                        {/* Claim Excess - Modal Field */}
                        <div>
                          <label htmlFor="claimExcess" className="block text-sm font-medium text-gray-700 mb-1">
                            Claim Excess <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500">R</span>
                            </div>
                            <input
                              type="number"
                              id="claimExcess"
                              value={formData.claimExcess}
                              onChange={(e) => setFormData(prev => ({ ...prev, claimExcess: e.target.value }))}
                              className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Creating...' : 'Submit'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Style to ensure date picker appears above modal */}
            <style>{`
        /* Ensure date picker appears correctly in modal */
        input[type="date"] {
          position: relative;
        }
        input[type="date"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          position: relative;
          z-index: 1;
        }
        /* Fix for the modal - ensure date picker shows on top */
        .fixed.inset-0.z-\\[9999\\] {
          pointer-events: none;
        }
        .fixed.inset-0.z-\\[9999\\] > div {
          pointer-events: auto;
        }
        .fixed.inset-0.z-\\[9999\\] input[type="date"] {
          pointer-events: auto;
        }
        input[type="date"]::-webkit-datetime-edit {
          padding: 0;
        }
        input[type="date"] {
          min-height: 38px;
        }
      `}</style>

      {/* ✅ PRODUCT DETAILS MODAL - IN LIST VIEW */}
      {isViewProductModalVisible && productToView && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-[9999]">
          <div className="relative bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-4 border-b">
              <h3 className="text-2xl font-bold text-gray-800">
                {getProductTypeDisplay(productToView.product.type)} Details
              </h3>
              <button
                onClick={() => {
                  setIsViewProductModalVisible(false);
                  setProductToView(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Product details content - Same as detail view */}
            <div className="space-y-4">
              {productToView.product.type === 'properties' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.addressLine1 || productToView.product.data?.address_line_1 || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.addressLine2 || productToView.product.data?.address_line_2 || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.suburb || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.city || productToView.product.data?.city_town || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.postalCode || productToView.product.data?.postal_code || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Region</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.region || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Country</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.country || 'South Africa'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.propertyType || productToView.product.data?.property_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Residence Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.residenceType || productToView.product.data?.residence_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Building Age</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.buildingAge || productToView.product.data?.building_age || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                </div>
              )}

              {productToView.product.type === 'vehicles' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vehicle_type || productToView.product.data?.vehicleType || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.registration_number || productToView.product.data?.registrationNumber || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Make</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.make || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Model</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.model || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.year || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Variant</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.variant || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Color</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.color || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Usage</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.usage || productToView.product.data?.vehicle_usage || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">VIN Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vin_number || productToView.product.data?.vinNumber || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Engine Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.engine_number || productToView.product.data?.engineNumber || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">MMCode</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.mmcode || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.cover_type || productToView.product.data?.coverType || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insured_value || productToView.product.data?.insuredValue || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                </div>
              )}

              {productToView.product.type === 'trailer-caravans' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Trailer/Caravan Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vehicleType || productToView.product.data?.vehicle_type || productToView.product.data?.trailer_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.registrationNumber || productToView.product.data?.registration_number || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Make</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.make || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Model</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.model || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.year || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Use Retail Value</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.useRetailValue || productToView.product.data?.use_retail_value ? 'Yes' : 'No'}</p></div>
                </div>
              )}

              {productToView.product.type === 'risk-items' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.itemType || productToView.product.data?.item_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.itemName || productToView.product.data?.item_name || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.serialNumber || productToView.product.data?.serial_number || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.description || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">No Claim Bonus</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.noClaimsBonus || productToView.product.data?.no_claims_bonus || 'N/A'}</p></div>
                </div>
              )}

              {productToView.product.type === 'household-contents' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.addressLine1 || productToView.product.data?.address_line_1 || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.suburb || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">City/Town</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.cityTown || productToView.product.data?.city_town || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.propertyType || productToView.product.data?.property_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Cover Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.coverType || productToView.product.data?.cover_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Insured Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.insuredValue || productToView.product.data?.insured_value || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Excess</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.excess || 0)}</p></div>
                </div>
              )}

              {productToView.product.type === 'water-craft' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Vessel Name</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.vesselName || productToView.product.data?.vessel_name || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Craft Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.craftType || productToView.product.data?.craft_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Hull Make</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.hullMake || productToView.product.data?.hull_make || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.year || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Total Sum Insured</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.totalSumInsured || productToView.product.data?.total_sum_insured || 0)}</p></div>
                </div>
              )}

              {productToView.product.type === 'value-added' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.productType || productToView.product.data?.product_type || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{productToView.product.data?.description || 'N/A'}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Value</label><p className="text-gray-900 font-medium text-green-600 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.value || 0)}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Premium</label><p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">{formatCurrency(productToView.product.data?.premium || 0)}</p></div>
                </div>
              )}

              {!['properties', 'vehicles', 'trailer-caravans', 'risk-items', 'household-contents', 'water-craft', 'value-added'].includes(productToView.product.type) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Data</label>
                    <div className="p-4 bg-gray-50 rounded-lg border overflow-x-auto max-h-60 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(productToView.product.data, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setIsViewProductModalVisible(false);
                  setProductToView(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientClaimsTab;